import React, { useEffect, useRef, useId } from 'react';
import { animate, useMotionValue, AnimationPlaybackControls } from 'framer-motion';
function mapRange(value: number, fromLow: number, fromHigh: number, toLow: number, toHigh: number): number {
  if (fromLow === fromHigh) return toLow;
  const percentage = (value - fromLow) / (fromHigh - fromLow);
  return toLow + percentage * (toHigh - toLow);
}
interface EtherealShadowProps {
  color?: string;
  scale?: number;
  speed?: number;
  noiseOpacity?: number;
  noiseScale?: number;
  sizing?: 'fill' | 'stretch';
  className?: string;
}
export function EtherealShadow({
  color = 'rgba(232, 76, 26, 0.7)',
  scale = 100,
  speed = 90,
  noiseOpacity = 0.8,
  noiseScale = 1.2,
  sizing = 'fill',
  className = ''
}: EtherealShadowProps) {
  const rawId = useId();
  const id = `ethereal-${rawId.replace(/:/g, '')}`;
  const feColorMatrixRef = useRef<SVGFEColorMatrixElement>(null);
  const hueRotateMotionValue = useMotionValue(180);
  const hueRotateAnimation = useRef<AnimationPlaybackControls | null>(null);
  const displacementScale = mapRange(scale, 1, 100, 20, 100);
  const animationDuration = mapRange(speed, 1, 100, 1000, 50);
  useEffect(() => {
    if (!feColorMatrixRef.current) return;
    if (hueRotateAnimation.current) {
      hueRotateAnimation.current.stop();
    }
    hueRotateMotionValue.set(0);
    hueRotateAnimation.current = animate(hueRotateMotionValue, 360, {
      duration: animationDuration / 25,
      repeat: Infinity,
      repeatType: 'loop',
      repeatDelay: 0,
      ease: 'linear',
      delay: 0,
      onUpdate: (value: number) => {
        if (feColorMatrixRef.current) {
          feColorMatrixRef.current.setAttribute('values', String(value));
        }
      }
    });
    return () => {
      if (hueRotateAnimation.current) {
        hueRotateAnimation.current.stop();
      }
    };
  }, [animationDuration, hueRotateMotionValue]);
  return <div className={className} style={{
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    height: '100%'
  }} data-id="element-564">
      <div style={{
      position: 'absolute',
      inset: -displacementScale,
      filter: `url(#${id}) blur(4px)`
    }} data-id="element-565">
        <svg style={{
        position: 'absolute'
      }} data-id="element-566">
          <defs data-id="element-567">
            <filter id={id} data-id="element-568">
              <feTurbulence result="undulation" numOctaves={2} baseFrequency={`${mapRange(scale, 0, 100, 0.001, 0.0005)},${mapRange(scale, 0, 100, 0.004, 0.002)}`} seed={0} type="turbulence" data-id="element-569" />
              <feColorMatrix ref={feColorMatrixRef} in="undulation" type="hueRotate" values="180" data-id="element-570" />
              <feColorMatrix in="dist" result="circulation" type="matrix" values="4 0 0 0 1  4 0 0 0 1  4 0 0 0 1  1 0 0 0 0" data-id="element-571" />
              <feDisplacementMap in="SourceGraphic" in2="circulation" scale={displacementScale} result="dist" data-id="element-572" />
              <feDisplacementMap in="dist" in2="undulation" scale={displacementScale} result="output" data-id="element-573" />
            </filter>
          </defs>
        </svg>
        <div style={{
        backgroundColor: color,
        maskImage: `url('https://framerusercontent.com/images/ceBGguIpUU8luwByxuQz79t7To.png')`,
        WebkitMaskImage: `url('https://framerusercontent.com/images/ceBGguIpUU8luwByxuQz79t7To.png')`,
        maskSize: sizing === 'stretch' ? '100% 100%' : 'cover',
        WebkitMaskSize: sizing === 'stretch' ? '100% 100%' : 'cover',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        width: '100%',
        height: '100%'
      }} data-id="element-574" />
      </div>
      {noiseOpacity > 0 && <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: `url("https://framerusercontent.com/images/g0QcWrxr87K0ufOxIUFBakwYA8.png")`,
      backgroundSize: noiseScale * 200,
      backgroundRepeat: 'repeat',
      opacity: noiseOpacity / 2
    }} data-id="element-575" />}
    </div>;
}